import { useState } from "react";
import { t } from "@lingui/core/macro";

import { Button } from "_/components/button";

import * as S from "./styled";

type CopyState = "notAttempted" | "copied" | "error";

/**
 * Copies the current page URL to the clipboard. The logs view keeps its
 * submitted query (search text, levels, and time range) in the URL, so the
 * link reproduces the same view for whoever opens it.
 */
export const ShareLinkButton = () => {
  const [copyState, setCopyState] = useState<CopyState>("notAttempted");

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyState("copied");
    } catch {
      setCopyState("error");
      console.error("Could not copy share link to clipboard.");
    }
    setTimeout(() => setCopyState("notAttempted"), 3000);
  }

  const iconVariant = (
    {
      notAttempted: "Share",
      copied: "CheckCircle",
      error: "Error",
    } as const
  )[copyState];

  const label = {
    notAttempted: t`components.machine-logs.share-link`,
    copied: t`components.machine-logs.link-copied`,
    error: t`components.machine-logs.copy-link-failed`,
  }[copyState];

  return (
    <Button
      type="button"
      size="small"
      kind="secondary"
      iconOnly
      title={label}
      aria-label={label}
      aria-live="polite"
      onClick={() => void copyLink()}
    >
      <S.CopyStatusIcon
        variant={iconVariant}
        $copied={copyState === "copied"}
      />
    </Button>
  );
};
